/** UTM grid overlay — item 17 (feature catalog 1f: "how the rural West
 * actually describes land"), the UTM half. Runtime-generated, never baked:
 * a pure WGS84 lat/lon <-> UTM projection plus a grid-line generator for
 * whatever viewport the map is currently showing. MapCanvas feeds this from
 * the camera on `moveend` (idle) — there is no utm.pmtiles; the grid is
 * math, not data, so it's available for every state with zero factory/
 * download cost ("UTM always available — it's math").
 *
 * Forward/inverse formulas are the standard Snyder transverse-Mercator
 * series (USGS Professional Paper 1395) on the WGS84 ellipsoid. Ported
 * line-for-line from ios/Dustlark/Map/UTMGrid.swift — the math must stay
 * identical between the two; a drift is a bug. Verified (not just
 * transcribed) against 3 published fixtures before landing in either
 * language; see utm-grid.test.ts for the exact numbers and sources.
 *
 * Northern-hemisphere only — every Dustlark region is in the continental
 * US, so the southern-hemisphere 10,000,000 m northing offset is never
 * needed. Norway/Svalbard's irregular zone widths are similarly out of
 * scope — US-only zones are always a clean 6 degrees wide.
 */

export interface LatLon {
  lat: number;
  lon: number;
}

// ------------------------------------------------------- WGS84/UTM constants

const A = 6_378_137.0; // semi-major axis, m
const F = 1 / 298.257223563; // flattening
const B = A * (1 - F); // semi-minor axis, m
const E2 = 1 - (B * B) / (A * A); // eccentricity^2
const EP2 = E2 / (1 - E2); // second eccentricity^2
const K0 = 0.9996; // central-meridian scale factor
const FALSE_EASTING = 500_000.0;

/** UTM zone number (1...60) for a longitude, standard 6-degree slicing. */
export function zoneForLongitude(lon: number): number {
  return Math.floor((lon + 180) / 6) + 1;
}

/** A zone's central meridian, degrees. Zone N spans [-183+6N, -177+6N]. */
export function centralMeridian(zone: number): number {
  return -183 + 6 * zone;
}

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

// -------------------------------------------- forward: lat/lon -> UTM E/N

export function toUTM(lat: number, lon: number, zone: number): { easting: number; northing: number } {
  const lam0 = toRad(centralMeridian(zone));
  const phi = toRad(lat);
  const lam = toRad(lon);

  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const tanPhi = Math.tan(phi);
  const N = A / Math.sqrt(1 - E2 * sinPhi * sinPhi);
  const T = tanPhi * tanPhi;
  const C = EP2 * cosPhi * cosPhi;
  const Acoef = cosPhi * (lam - lam0);

  const M =
    A *
    ((1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 ** 3) / 256) * phi -
      (3 * E2 / 8 + (3 * E2 * E2) / 32 + (45 * E2 ** 3) / 1024) * Math.sin(2 * phi) +
      ((15 * E2 * E2) / 256 + (45 * E2 ** 3) / 1024) * Math.sin(4 * phi) -
      ((35 * E2 ** 3) / 3072) * Math.sin(6 * phi));

  const easting =
    K0 * N * (Acoef + ((1 - T + C) * Acoef ** 3) / 6 + ((5 - 18 * T + T * T + 72 * C - 58 * EP2) * Acoef ** 5) / 120) +
    FALSE_EASTING;

  const northing =
    K0 *
    (M +
      N *
        tanPhi *
        ((Acoef * Acoef) / 2 +
          ((5 - T + 9 * C + 4 * C * C) * Acoef ** 4) / 24 +
          ((61 - 58 * T + T * T + 600 * C - 330 * EP2) * Acoef ** 6) / 720));

  return { easting, northing };
}

// -------------------------------------------- inverse: UTM E/N -> lat/lon

export function toLatLon(easting: number, northing: number, zone: number): LatLon {
  const lam0 = toRad(centralMeridian(zone));
  const x = easting - FALSE_EASTING;
  const y = northing;

  const M = y / K0;
  const mu = M / (A * (1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 ** 3) / 256));
  const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));

  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
    ((21 * e1 * e1) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
    ((151 * e1 ** 3) / 96) * Math.sin(6 * mu) +
    ((1097 * e1 ** 4) / 512) * Math.sin(8 * mu);

  const sinPhi1 = Math.sin(phi1);
  const cosPhi1 = Math.cos(phi1);
  const tanPhi1 = Math.tan(phi1);
  const N1 = A / Math.sqrt(1 - E2 * sinPhi1 * sinPhi1);
  const T1 = tanPhi1 * tanPhi1;
  const C1 = EP2 * cosPhi1 * cosPhi1;
  const R1 = (A * (1 - E2)) / Math.pow(1 - E2 * sinPhi1 * sinPhi1, 1.5);
  const D = x / (N1 * K0);

  const phi =
    phi1 -
    ((N1 * tanPhi1) / R1) *
      ((D * D) / 2 -
        ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * EP2) * D ** 4) / 24 +
        ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * EP2 - 3 * C1 * C1) * D ** 6) / 720);

  const lam =
    lam0 +
    (D - ((1 + 2 * T1 + C1) * D ** 3) / 6 + ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * EP2 + 24 * T1 * T1) * D ** 5) / 120) /
      cosPhi1;

  return { lat: toDeg(phi), lon: toDeg(lam) };
}

// ------------------------------------------------------------ grid lines

export type Axis = "easting" | "northing";

export interface GridLine {
  axis: Axis;
  zone: number;
  /** The constant easting/northing this line traces, meters. */
  valueM: number;
  /** Polyline in lat/lon, already clipped to the viewport (and, for a
   * viewport spanning a zone boundary, to this line's own zone slice). */
  path: LatLon[];
  /** Edge label per USGS convention, kept simple per spec: the kilometer
   * value plus an axis letter, e.g. "442E", "4442N". */
  label: string;
}

/** 1 km at zoom 12+ (close enough to read individual grid cells), 10 km
 * from the app's own zoom floor up to 12 (regional views — a 1 km grid
 * there would be thousands of lines). Mirrors UTMGrid.spacing(forZoom:). */
export function spacingForZoom(zoom: number): number {
  return zoom >= 12 ? 1_000 : 10_000;
}

// A grid line spans at most ~10 km (10 km spacing) inside one zone, where a
// meridian/parallel's curvature is imperceptible at any zoom this app
// renders; a few samples keep the polyline visually smooth without doing
// needless inverse-projection work.
const SAMPLES_PER_LINE = 5;

// Safety valve against a runaway line count if ever called with an
// unexpectedly huge viewport — never hit in normal use (spacing is
// zoom-gated to keep the count small; see spacingForZoom).
const MAX_LINES = 300;

/** Every UTM grid line touching [west,south,east,north] at the given zoom.
 * Zone-aware: a viewport straddling a zone boundary (e.g. Utah's southwest
 * sliver sits in zone 11 while the rest of the state is zone 12) gets each
 * zone's own lines, clipped to that zone's slice of the viewport — the same
 * visible stagger a printed UTM grid shows at a zone line, not a bug to
 * paper over. Mirrors UTMGrid.gridLines line-for-line. */
export function gridLines(west: number, south: number, east: number, north: number, zoom: number): GridLine[] {
  if (!(west < east) || !(south < north)) return [];
  const step = spacingForZoom(zoom);
  const zoneWest = zoneForLongitude(west);
  const zoneEast = zoneForLongitude(east);
  if (zoneWest > zoneEast) return [];

  const lines: GridLine[] = [];
  for (let z = zoneWest; z <= zoneEast; z++) {
    if (lines.length >= MAX_LINES) break;
    const cm = centralMeridian(z);
    const clipWest = Math.max(west, cm - 3);
    const clipEast = Math.min(east, cm + 3);
    if (!(clipWest < clipEast)) continue;

    const corners: [number, number][] = [
      [clipWest, south],
      [clipEast, south],
      [clipWest, north],
      [clipEast, north],
    ];
    const utm = corners.map(([lon, lat]) => toUTM(lat, lon, z));
    const minE = Math.min(...utm.map((u) => u.easting));
    const maxE = Math.max(...utm.map((u) => u.easting));
    const minN = Math.min(...utm.map((u) => u.northing));
    const maxN = Math.max(...utm.map((u) => u.northing));

    for (let e = Math.ceil(minE / step) * step; e <= maxE && lines.length < MAX_LINES; e += step) {
      const path: LatLon[] = [];
      for (let i = 0; i <= SAMPLES_PER_LINE; i++) {
        const n = minN + ((maxN - minN) * i) / SAMPLES_PER_LINE;
        path.push(toLatLon(e, n, z));
      }
      lines.push({ axis: "easting", zone: z, valueM: e, path, label: `${Math.round(e / 1000)}E` });
    }

    for (let n = Math.ceil(minN / step) * step; n <= maxN && lines.length < MAX_LINES; n += step) {
      const path: LatLon[] = [];
      for (let i = 0; i <= SAMPLES_PER_LINE; i++) {
        const e = minE + ((maxE - minE) * i) / SAMPLES_PER_LINE;
        path.push(toLatLon(e, n, z));
      }
      lines.push({ axis: "northing", zone: z, valueM: n, path, label: `${Math.round(n / 1000)}N` });
    }
  }
  return lines;
}

// Minimal local GeoJSON shape rather than the ambient `GeoJSON.*` namespace
// — same rationale/precedent as RouteOverlay.ts's own LineFC etc.: not
// guaranteed to resolve under this project's narrowed tsconfig `types`
// list. Cast at the MapLibre boundary (MapCanvas.tsx) rather than fighting
// the style-spec's deeply recursive expression types.
export interface GridLineFC {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { axis: Axis; zone: number; valueM: number; label: string };
    geometry: { type: "LineString"; coordinates: [number, number][] };
  }[];
}

/** gridLines(), packaged as a GeoJSON FeatureCollection of LineStrings —
 * the shape MapCanvas feeds straight to a MapLibre GeoJSON source. Kept as
 * a thin wrapper over the pure gridLines() above (still pure — no map/DOM
 * access) so utm-grid.test.ts can assert on the plain line data without
 * threading GeoJSON shape assertions through every test. */
export function gridLinesGeoJSON(
  west: number,
  south: number,
  east: number,
  north: number,
  zoom: number,
): GridLineFC {
  const lines = gridLines(west, south, east, north, zoom);
  return {
    type: "FeatureCollection",
    features: lines.map((line) => ({
      type: "Feature",
      properties: { axis: line.axis, zone: line.zone, valueM: line.valueM, label: line.label },
      geometry: {
        type: "LineString",
        coordinates: line.path.map((p) => [p.lon, p.lat] as [number, number]),
      },
    })),
  };
}
